/**
 * Date and number formatting.
 *
 * Everything is relative where a student would think relatively ("in 2 days",
 * "3h") and absolute where they would not (an event's actual start). The seed
 * stores ISO strings throughout.
 */

const DAY_MS = 86_400_000;

function parse(iso?: string): Date | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : new Date(t);
}

/** "2h", "3d", "Aug 12" — compact attribution timestamps. */
export function relativeShort(iso?: string, now = new Date()): string {
  const date = parse(iso);
  if (!date) return "";

  const diff = now.getTime() - date.getTime();
  if (diff < 0) return absoluteDay(iso);

  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return "now";
  if (minutes < 60) return `${minutes}m`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;

  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;

  return absoluteDay(iso);
}

/** "Thu Aug 20 · 7 PM" */
export function eventWhen(iso?: string, endIso?: string): string {
  const date = parse(iso);
  if (!date) return "";

  const day = date.toLocaleDateString(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
  const start = timeOfDay(date);
  const end = parse(endIso);

  if (!end) return `${day} · ${start}`;
  // Same-day ranges collapse to one date with a time span.
  const sameDay = end.toDateString() === date.toDateString();
  return sameDay ? `${day} · ${start}–${timeOfDay(end)}` : `${day} · ${start}`;
}

function timeOfDay(date: Date): string {
  return date
    .toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
    .replace(":00", "");
}

/** "Aug 20" */
export function absoluteDay(iso?: string): string {
  const date = parse(iso);
  if (!date) return "";
  return date.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * "due in 2 days", "due tomorrow", "overdue".
 *
 * Deliberately day-granular: a deadline at 11:59 PM tonight is "due today", not
 * "due in 7 hours" — the second reads as a countdown and lands as pressure.
 */
export function dueIn(iso?: string, now = new Date()): string {
  const date = parse(iso);
  if (!date) return "";

  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const startOfDue = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();
  const days = Math.round((startOfDue - startOfToday) / DAY_MS);

  if (days < 0) return "overdue";
  if (days === 0) return "due today";
  if (days === 1) return "due tomorrow";
  if (days < 14) return `due in ${days} days`;
  return `due ${absoluteDay(iso)}`;
}

/** Whole days remaining, for deadline urgency. */
export function daysUntil(iso?: string, now = new Date()): number | null {
  const date = parse(iso);
  if (!date) return null;
  return Math.ceil((date.getTime() - now.getTime()) / DAY_MS);
}

/** 1250 -> "1,250" */
export function count(n: number): string {
  return n.toLocaleString();
}

/** 4230 -> "4.2k" for dense metadata rows. */
export function compactCount(n: number): string {
  if (n < 1000) return String(n);
  if (n < 1_000_000) return `${(n / 1000).toFixed(n < 10_000 ? 1 : 0)}k`;
  return `${(n / 1_000_000).toFixed(1)}m`;
}

/** 2500 -> "$2,500". Scholarship and job amounts only — never reward points. */
export function money(amount: number): string {
  return `$${Math.round(amount).toLocaleString()}`;
}

/** 0.93 -> "93%" */
export function percent(fraction: number): string {
  return `${Math.round(fraction * 100)}%`;
}
